import React, { useState } from "react";
import { SubPageHeader } from "../../../components/ui/SubPageHeader";
import {
  FileText,
  Printer,
  Download,
  MapPin,
  Building2,
  Calendar,
} from "lucide-react";
import { Button } from "../../../components/ui/Button";
import { format, subDays } from "date-fns";
import { useAuth } from "../../../auth/AuthContext";

interface XReportData {
  businessInfo: {
    name: string;
    address: string;
    reportStart: Date;
    reportEnd: Date;
  };
  salesAndTaxes: {
    numberOfSales: number;
    totalNetSale: number;
    totalTax: number;
    totalSales: number;
    totalQuickPayments: number;
  };
  tender: {
    items: { method: string; in: number; out: number; amount: number }[];
    totalTendered: number;
  };
  salesByService: {
    items: { name: string; quantity: number; amount: number }[];
    totalNet: number;
    total: number;
  };
  salesByEmployee: {
    items: { name: string; amount: number }[];
    total: number;
  };
  refundsAndDiscounts: {
    refundsNet: number;
    refundsTaxGross: number;
    totalDiscount: number;
    totalRefunds: number;
  };
}

const mockXReportData: XReportData = {
  businessInfo: {
    name: "My Salon Business",
    address: "123 Business Road, City, Countrry",
    reportStart: subDays(new Date(), 0),
    reportEnd: new Date(),
  },
  salesAndTaxes: {
    numberOfSales: 4,
    totalNetSale: 407.0,
    totalTax: 0.0,
    totalSales: 407.0,
    totalQuickPayments: 0.0,
  },

  tender: {
    items: [
      { method: "Cash", in: 0.0, out: 0.0, amount: 0.0 },
      { method: "Cheque", in: 0.0, out: 0.0, amount: 0.0 },
      { method: "Card", in: 407.0, out: 0.0, amount: 407.0 },
    ],
    totalTendered: 407.0,
  },
  salesByService: {
    items: [
      {
        name: "Adults Haircut with or without Beard",
        quantity: 1,
        amount: 25.0,
      },
      { name: "Full Head Colour", quantity: 2, amount: 180.0 },
      { name: "Keratin Treatment", quantity: 1, amount: 202.0 },
    ],
    totalNet: 407.0,
    total: 407.0,
  },
  salesByEmployee: {
    items: [
      { name: "Staff Member A", amount: 407.0 },
      { name: "Staff Member B", amount: 0.0 },
    ],
    total: 407.0,
  },
  refundsAndDiscounts: {
    refundsNet: 0.0,
    refundsTaxGross: 0.0,
    totalDiscount: 0.0,
    totalRefunds: 0.0,
  },
};

const CurrencyDisplay = ({
  amount,
  bold = false,
}: {
  amount: number;
  bold?: boolean;
}) => {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";

  return (
    <span
      className={`${bold ? "font-bold text-gray-900" : "font-medium text-gray-700"
        }`}
    >
      {currencySymbol}{amount.toFixed(2)}
    </span>
  );
};

const SectionHeader = ({ title }: { title: string }) => (
  <div className="border-b-2 border-[#885ABB]/20 pb-2 mb-4 mt-6">
    <h3 className="text-[#885ABB] font-bold text-sm tracking-uppercase uppercase tracking-wider">
      {title}
    </h3>
  </div>
);

const RowItem = ({
  label,
  value,
  bold = false,
  subLabel = "",
}: {
  label: string;
  value: React.ReactNode;
  bold?: boolean;
  subLabel?: string;
}) => (
  <div
    className={`flex justify-between items-center py-2 ${bold ? "border-t border-gray-100 mt-1 pt-3" : ""
      }`}
  >
    <div className="flex flex-col">
      <span
        className={`${bold ? "font-bold text-gray-900" : "text-gray-600 font-medium"
          } text-sm `}
      >
        {label}
      </span>
      {subLabel && <span className="text-xs text-gray-400">{subLabel}</span>}
    </div>
    {value}
  </div>
);

const XReport: React.FC = () => {
  const data = mockXReportData;

  return (
    <div className="flex-1 flex flex-col space-y-4 lg:space-y-6 p-4 lg:p-6">
      {/* Page Header */}
      <SubPageHeader
        title="X-Report"
        subtitle="Snapshot of daily sales activity"
        icon={FileText}
        actions={
          <div className="flex items-center gap-3">
            <Button
              className="bg-[#885ABB] hover:bg-[#7748A4] text-white flex items-center gap-2 px-3 lg:px-4 py-2 shadow-sm rounded-xl text-xs lg:text-sm font-bold"
              title="Print Report"
            >
              <Printer size={16} className="lg:w-18 lg:h-18" />
              <span className="hidden sm:inline">Print Report</span>
            </Button>
          </div>
        }
      />

      {/* Report Content - Centered Card */}
      <div className="flex-1 flex justify-center pb-8">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 max-w-3xl w-full p-6 sm:p-8 md:p-12 relative overflow-hidden">
          {/* Decorative Top Accent */}
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#885ABB]"></div>

          {/* Header */}
          <div className="text-center mb-6 lg:mb-8 pb-6 border-b border-gray-100">
            <h1 className="text-xl lg:text-2xl font-black text-[#402B69] mb-2 uppercase tracking-tight">
              X-Out REPORT
            </h1>
            <h2 className="text-base lg:text-lg font-bold text-[#885ABB] mb-1">
              {data.businessInfo.name}
            </h2>
            <div className="flex items-center justify-center gap-1 text-gray-400 text-[10px] lg:text-sm mb-4">
              <MapPin size={12} className="opacity-70" />
              <span className="font-medium">{data.businessInfo.address}</span>
            </div>
            <div className="inline-flex items-center gap-2 bg-purple-50 text-[#885ABB] text-[10px] lg:text-xs font-bold px-4 py-2 rounded-full border border-purple-100/50">
              <Calendar size={12} />
              <div className="flex flex-col sm:flex-row sm:gap-1 text-center">
                <span>Report run from:</span>
                <span className="font-black">
                  {format(data.businessInfo.reportStart, "dd/MM/yyyy HH:mm")} - {format(data.businessInfo.reportEnd, "dd/MM/yyyy HH:mm")}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* Section 1: Sales and Taxes */}
            <div>
              <SectionHeader title="Sales and Taxes Summary" />
              <div className="space-y-1">
                <RowItem
                  label="Number of Sales"
                  value={
                    <span className="text-gray-900 font-bold">
                      {data.salesAndTaxes.numberOfSales}
                    </span>
                  }
                />
                <RowItem
                  label="Total Net Sale"
                  value={
                    <CurrencyDisplay amount={data.salesAndTaxes.totalNetSale} />
                  }
                />
                <RowItem
                  label="Total Tax"
                  value={<CurrencyDisplay amount={data.salesAndTaxes.totalTax} />}
                />
                <RowItem
                  label="Total Sales"
                  value={
                    <CurrencyDisplay amount={data.salesAndTaxes.totalSales} bold />
                  }
                  bold
                />
                <RowItem
                  label="Total Quick Payments"
                  value={
                    <CurrencyDisplay
                      amount={data.salesAndTaxes.totalQuickPayments}
                    />
                  }
                />
              </div>
            </div>

            {/* Section 2: Tender */}
            <div>
              <SectionHeader title="Tender" />
              <div className="w-full">
                <div className="hidden sm:grid grid-cols-4 gap-4 text-[10px] lg:text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-3 px-2 border-b border-gray-50 pb-2">
                  <div className="col-span-1">Tender</div>
                  <div className="col-span-1 text-right">In</div>
                  <div className="col-span-1 text-right">Out</div>
                  <div className="col-span-1 text-right">Amount</div>
                </div>
                <div className="space-y-3 sm:space-y-2">
                  {data.tender.items.map((item, idx) => (
                    <div key={idx} className="flex flex-col sm:grid sm:grid-cols-4 gap-2 sm:gap-4 py-2 sm:py-1 px-2 bg-gray-50/50 sm:bg-transparent rounded-xl sm:rounded-none">
                      <div className="flex justify-between sm:block border-b sm:border-0 border-gray-100 pb-2 sm:pb-0">
                        <span className="sm:hidden text-[10px] uppercase text-gray-400 font-bold">Method</span>
                        <span className="font-bold sm:font-medium text-[#402B69] sm:text-gray-700">{item.method}</span>
                      </div>
                      <div className="flex justify-between sm:block">
                        <span className="sm:hidden text-[10px] uppercase text-gray-400 font-bold">In</span>
                        <div className="sm:text-right text-gray-600 font-medium">
                          <CurrencyDisplay amount={item.in} />
                        </div>
                      </div>
                      <div className="flex justify-between sm:block">
                        <span className="sm:hidden text-[10px] uppercase text-gray-400 font-bold">Out</span>
                        <div className="sm:text-right text-gray-600 font-medium">
                          <CurrencyDisplay amount={item.out} />
                        </div>
                      </div>
                      <div className="flex justify-between sm:block">
                        <span className="sm:hidden text-[10px] uppercase text-gray-400 font-bold">Amount</span>
                        <div className="sm:text-right font-bold text-[#885ABB]">
                          <CurrencyDisplay amount={item.amount} bold />
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-4 lg:pt-3 mt-2 border-t border-gray-100 px-2 group">
                    <span className="font-bold text-[#402B69] uppercase text-xs tracking-wider">Total Tendered</span>
                    <CurrencyDisplay amount={data.tender.totalTendered} bold />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Sales By Service */}
            <div>
              <SectionHeader title="Sales By Service" />
              <div className="space-y-1">
                {data.salesByService.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center py-2 px-2 hover:bg-gray-50/50 rounded-lg transition-colors group">
                    <span className="text-gray-700 font-medium text-sm pr-4">
                      {item.name}
                      <span className="text-xs text-[#885ABB] bg-purple-50 px-2 py-0.5 rounded-full ml-2 font-bold whitespace-nowrap">
                        x{item.quantity}
                      </span>
                    </span>
                    <CurrencyDisplay amount={item.amount} />
                  </div>
                ))}
                <div className="mt-3 space-y-1">
                  <RowItem
                    label="Total Net"
                    value={
                      <CurrencyDisplay amount={data.salesByService.totalNet} bold />
                    }
                    bold
                  />
                  <div className="flex justify-between items-center py-2 px-2 pt-3 border-t border-gray-50">
                    <span className="font-bold text-[#402B69] text-sm uppercase tracking-wider">Total</span>
                    <CurrencyDisplay amount={data.salesByService.total} bold />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4: Sales By Employee */}
            <div>
              <SectionHeader title="Sales By Employee" />
              <div className="space-y-1">
                {data.salesByEmployee.items.map((emp, idx) => (
                  <RowItem
                    key={idx}
                    label={emp.name}
                    value={<CurrencyDisplay amount={emp.amount} />}
                  />
                ))}
                <RowItem
                  label="Total"
                  value={
                    <CurrencyDisplay amount={data.salesByEmployee.total} bold />
                  }
                  bold
                />
              </div>
            </div>

            {/* Section 5: Refunds & Discounts */}
            <div className="pb-4">
              <SectionHeader title="Refunds & Discounts Summary" />
              <div className="space-y-1">
                <RowItem
                  label="Total Refunds Net"
                  value={
                    <CurrencyDisplay amount={data.refundsAndDiscounts.refundsNet} />
                  }
                />
                <RowItem
                  label="Total Refunds Tax Gross"
                  value={
                    <CurrencyDisplay
                      amount={data.refundsAndDiscounts.refundsTaxGross}
                    />
                  }
                />
                <RowItem
                  label="Total Discount"
                  value={
                    <CurrencyDisplay
                      amount={data.refundsAndDiscounts.totalDiscount}
                    />
                  }
                />
                <RowItem
                  label="Total Refunds"
                  value={
                    <div className="text-[#EF4444]">
                      <CurrencyDisplay
                        amount={data.refundsAndDiscounts.totalRefunds}
                        bold
                      />
                    </div>
                  }
                  bold
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default XReport;
